import React from 'react';
import './FormAccount.scss';
import { MenuView } from 'Store/User/reducer';
import Button from 'Components/Shared/Button/Button';
import InputField from 'Components/Shared/InputField/InputField';
import FormattedText from 'Components/Shared/FormattedText/FormattedText';
import WarningWrapper from 'Components/Shared/WarningWrapper/WarningWrapper';
import FormNewsLetter from 'Components/FormNewsLetter/FormNewsLetter';
import FormCustomerTerms from 'Components/FormCustomerTerms/FormCustomerTerms';
import InputCountry from 'Components/InputCountry/InputCountry';

interface IProps {
  menuView: MenuView;
  isTermsWarning: boolean;
  isPasswordWarning: boolean;
  accountInput?: Julius.Website.CreateCustomerViewModel;
  passwordInput?: Julius.Website.ChangePasswordViewModel;
  onUserTermsChange: (isApproved: boolean) => void;
  onEmailTermsChange: (isApproved: boolean) => void;
  onChange: (e: React.FormEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onCountryChange: (value: string) => void;
}

const FormAccount: React.SFC<IProps> = ({
  menuView,
  isTermsWarning,
  isPasswordWarning,
  accountInput,
  passwordInput,
  onUserTermsChange,
  onEmailTermsChange,
  onChange,
  onSubmit,
  onCountryChange,
}) => {
  switch (menuView) {
    case MenuView.CREATE_ACCOUNT_SUCCESS:
      return (
        <div className='form-account'>
          <FormattedText 
            style='title'
            displayName='Skapa konto'
            formattedName='account.create.header'
          />
          <FormattedText 
            style='sub-title'
            displayName='Ditt konto är skapat. Alla bokningar du gör som inloggad kommer att finnas tillgängliga online.'
            formattedName='account.create.success'
          />
        </div>
      );
    case MenuView.UPDATE_ACCOUNT_SUCCESS:
      return (
        <div className='form-account'>
          <FormattedText 
            style='title'
            displayName='Ändra konto'
            formattedName='account.update.header'
          />
          <FormattedText 
            style='sub-title'
            displayName='Dina uppgifter har ändrats.'
            formattedName='account.update.success'
          />
        </div>
      );
    case MenuView.UPDATE_PASSWORD_SUCCESS:
      return (
        <div className='form-account'>
          <FormattedText 
            style='title'
            displayName='Ändra lösenord'
            formattedName='account.password.header'
          />
          <FormattedText 
            style='sub-title'
            displayName='Ditt lösenord är uppdaterat.'
            formattedName='account.password.update.success'
          />
        </div>
      );

    case MenuView.UPDATE_PASSWORD_FAILED:
      return (
        <div className='form-account'>
          <FormattedText 
            style='title'
            displayName='Ändra lösenord'
            formattedName='account.password.header'
          />
          <FormattedText 
            style='sub-title'
            displayName='Det gick inte att ändra lösenordet.'
            formattedName='account.password.failed'
          />
        </div>
      );
    case MenuView.CREATE_ACCOUNT_ALREADY_EXISTS:
      return (
        <div className='form-account'>
          <FormattedText 
            style='title'
            displayName='Skapa konto'
            formattedName='account.create.header'
          />
          <FormattedText 
            style='sub-title'
            displayName='Det verkar redan finnas en användare med den här e-postadressen.'
            formattedName='account.warning.taken'
          />
        </div>
      );
  }

  const isShowingWarning = isPasswordWarning || isTermsWarning;

  if (menuView === MenuView.UPDATE_PASSWORD) {
    return (
      <div className='form-account'>
          <FormattedText 
            style='title'
            displayName='Ändra lösenord'
            formattedName='account.password.header'
          />
          <FormattedText 
            style='sub-title'
            displayName='Fyll i ditt gamla och nya lösenord.'
            formattedName='account.password.text'
          />
        <form onSubmit={ onSubmit }>
          <InputField 
            type='password' 
            name='oldPassword' 
            value={ passwordInput.oldPassword }
            onChange={ onChange } 
            label={ {
              displayName: 'Nuvarande lösenord',
              formattedName: 'global.password.current',
            } }
            required={ true }
          />
          <InputField 
            type='password' 
            name='password' 
            value={ passwordInput.password }
            onChange={ onChange } 
            label={ {
              displayName: 'Nytt lösenord',
              formattedName: 'global.password.new',
            } }
            required={ true }
            minLength={ 6 }
          />
          <InputField 
            type='password' 
            name='password2' 
            value={ passwordInput.password2 }
            onChange={ onChange } 
            label={ {
              displayName: 'Nytt lösenord',
              formattedName: 'global.password.new',
            } }
            required={ true }
            minLength={ 6 }
          />
          <WarningWrapper 
            style='submit-button'
            isShowingWarning={ isPasswordWarning }
            displayName='Lösenorden måste matcha'
            formattedName='account.warning.password'
          >
            <Button type='submit'>
              <FormattedText
                displayName='Spara'
                formattedName='global.save'
              />
            </Button>
          </WarningWrapper>
        </form>
      </div>
    );
  }

  return (
    <div className='form-account'>
      { menuView === MenuView.CREATE_ACCOUNT ?
        <>
          <FormattedText 
            style='title'
            displayName='Skapa konto'
            formattedName='account.create.header'
          /> 
          <FormattedText 
            style='sub-title medium-bold'
            displayName='Fyll i dina uppgifter så blir det enklare att beställa biljetter.'
            formattedName='account.create.text'
          />
        </> :
        <>
          <FormattedText 
            style='title'
            displayName='Ändra konto'
            formattedName='account.update.header'
          />
          <FormattedText 
            style='sub-title'
            displayName='Fyll i dina nya uppgifter.'
            formattedName='account.update.text'
          />
        </>
      }
      <form onSubmit={ onSubmit }>
        <InputField 
          type='email' 
          name='email' 
          value={ accountInput.email }
          onChange={ onChange } 
          label={ {
            displayName: 'E-post',
            formattedName: 'global.email',
          } }
          required={ true }
        />
        { menuView === MenuView.CREATE_ACCOUNT &&
          <>
            <InputField 
              type='password' 
              name='password' 
              value={ accountInput.password }
              onChange={ onChange } 
              label={ {
                displayName: 'Lösenord',
                formattedName: 'global.password',
              } }
              required={ true }
              minLength={ 6 }
            />
            <InputField 
              type='password' 
              name='password2' 
              value={ accountInput.password2 }
              onChange={ onChange } 
              label={ {
                displayName: 'Lösenord',
                formattedName: 'global.password',
              } }
              required={ true }
              minLength={ 6 }
            />
          </>
        }
        <InputField 
          type='text' 
          name='firstName' 
          value={ accountInput.firstName }
          onChange={ onChange } 
          label={ {
            displayName: 'Förnamn',
            formattedName: 'global.firstname',
          } }
          required={ true }
        />
        <InputField 
          type='text' 
          name='lastName' 
          value={ accountInput.lastName }
          onChange={ onChange } 
          label={ {
            displayName: 'Efternamn',
            formattedName: 'global.lastname',
          } }
          required={ true }
        />
        <InputField 
          type='text' 
          name='phone' 
          value={ accountInput.phone }
          onChange={ onChange } 
          label={ {
            displayName: 'Mobilnummer',
            formattedName: 'global.phone',
          } }
          required={ true }
        />
        <InputField 
          type='text' 
          name='street' 
          value={ accountInput.street }
          onChange={ onChange } 
          label={ {
            displayName: 'Gata',
            formattedName: 'global.street',
          } }
          required={ false }
        />
        <InputField 
          type='text' 
          name='zipcode' 
          value={ accountInput.zipcode }
          onChange={ onChange } 
          label={ {
            displayName: 'Postnummer',
            formattedName: 'global.zipcode',
          } }
          required={ false }
        />
        <InputField 
          type='text' 
          name='city' 
          value={ accountInput.city }
          onChange={ onChange } 
          label={ {
            displayName: 'Ort',
            formattedName: 'global.city',
          } }
          required={ false }
        />
        <InputCountry 
          { ... { 
            initialCountryKey: accountInput.country, 
            onChange: onCountryChange,
          } } 
        />
        <InputField 
          type='text' 
          name='company' 
          value={ accountInput.company }
          onChange={ onChange } 
          label={ {
            displayName: 'Företag',
            formattedName: 'global.company',
          } }
          required={ false }
        />
        <FormCustomerTerms 
          onChange={ onUserTermsChange } 
          isChecked={ accountInput.isUserTermsApproved } 
        />
        <FormNewsLetter 
          onChange={ onEmailTermsChange } 
          isChecked={ accountInput.isEmailsAllowed }
        />
        <WarningWrapper 
          style='submit-button'
          isShowingWarning={ isShowingWarning }
          displayName={ 
            isPasswordWarning ? 
            'Lösenorden måste matcha' : 
            'Godkänn villkoren' 
          }
          formattedName={ 
            isPasswordWarning ? 
            'account.warning.password' : 
            'account.warning.terms'
          }
        >
          <Button type='submit'>
            <FormattedText
              displayName='Spara'
              formattedName='global.save'
            />
          </Button>
        </WarningWrapper>
      </form>
    </div>
  );
};

export default FormAccount;


// WEBPACK FOOTER //
// ./src/components/FormAccount/FormAccount.tsx